import {setVolume} from "@/redux/slices/mlSlices/chatSlice";

import {useEffect, useRef, useState} from "react";

import maxVol from "@images/chat/vol-max.svg";
import minVol from "@images/chat/vol-min.svg";
import muteVol from "@images/chat/vol-mute.svg";
import {BorderArticle} from "@ui/general/BorderArticle";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const Controls = () => {
  const [showControls, setShowControls] = useState(false);
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const {isMobile} = useSelector((state) => state.common.settings);
  const {volume} = useSelector((state) => state.ml.chat);

  const dispatch = useDispatch();
  const volumeRef = useRef(null);
  const volumeSliderRef = useRef(null);
  const controlsRef = useRef(null);

  const updateVolume = (nextVolume) => {
    const clampedVolume = Math.min(1, Math.max(0, nextVolume));
    dispatch(setVolume(Number(clampedVolume.toFixed(2))));
  };

  const updateVolumeFromPointer = (clientX, clientY) => {
    if (!volumeSliderRef.current) return;

    const rect = volumeSliderRef.current.getBoundingClientRect();

    if (isMobile) {
      const position = (rect.bottom - clientY) / rect.height;
      updateVolume(position);
      return;
    }

    const position = (clientX - rect.left) / rect.width;
    updateVolume(position);
  };

  const handleVolumePointerDown = (event) => {
    event.preventDefault();
    setIsDraggingVolume(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    updateVolumeFromPointer(event.clientX, event.clientY);
  };

  const handleVolumePointerMove = (event) => {
    if (!isDraggingVolume) return;
    updateVolumeFromPointer(event.clientX, event.clientY);
  };

  const handleVolumePointerUp = (event) => {
    if (!isDraggingVolume) return;
    setIsDraggingVolume(false);
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleVolumeKeyDown = (event) => {
    const step = 0.05;

    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      updateVolume(volume + step);
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      updateVolume(volume - step);
    }
  };

  useEffect(() => {
    if (volumeRef && volumeRef.current) {
      const clampedVolume = Math.min(1, Math.max(0, Number(volume) || 0));
      volumeRef.current.style.setProperty(
        "--i-progress",
        `${clampedVolume * 100}%`,
      );
    }
  }, [volume, volumeRef]);

  useEffect(() => {
    if (controlsRef && controlsRef.current) {
      controlsRef.current.style.height = showControls ? "auto" : "56rem";
    }
  }, [showControls, controlsRef]);

  return (
    <div className={styles.gameControlsWrap}>
      <BorderArticle>
        <div
          ref={controlsRef}
          className={styles.gameControls}
        >
          {isMobile && (
            <i
              onClick={() => setShowControls(!showControls)}
              className={styles.controlMobile}
            />
          )}

          <div>
            <div
              className={styles.controlImg}
              style={
                isMobile && !showControls ? {opacity: "0"} : {opacity: "1"}
              }
            >
              <img
                src={minVol}
                alt={"min"}
              />
            </div>

            <div
              ref={volumeRef}
              className={styles.gameVolume}
              style={
                isMobile && !showControls ? {opacity: "0"} : {opacity: "1"}
              }
            >
              <div
                ref={volumeSliderRef}
                className={styles.slider}
                role={"slider"}
                tabIndex={0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(volume * 100)}
                onPointerDown={handleVolumePointerDown}
                onPointerMove={handleVolumePointerMove}
                onPointerUp={handleVolumePointerUp}
                onPointerCancel={handleVolumePointerUp}
                onKeyDown={handleVolumeKeyDown}
              >
                <div className={styles.track} />

                <div
                  className={styles.fill}
                  style={
                    isMobile
                      ? {height: "var(--i-progress)"}
                      : {width: "var(--i-progress)"}
                  }
                />

                <i
                  className={styles.thumb}
                  style={
                    isMobile
                      ? {bottom: "var(--i-progress)"}
                      : {left: "var(--i-progress)"}
                  }
                />
              </div>
            </div>

            <div className={styles.controlImg}>
              <img
                src={maxVol}
                alt={"max"}
              />
            </div>
          </div>

          <div
            className={styles.controlImg}
            onClick={() => dispatch(setVolume(0))}
            style={isMobile && !showControls ? {opacity: "0"} : {opacity: "1"}}
          >
            <img
              style={{width: "27rem", height: "27rem"}}
              src={muteVol}
              alt={"mute"}
            />
          </div>
        </div>
      </BorderArticle>
    </div>
  );
};
